<!-- Profiles window (#profileModal): short text, one row per profile (photo or initial, name, bio, socials, save line,
     "Save online" / "Load online", active mark; pencil = profile card, disk = save to a file, import = load a file, bin
     for the other profiles; a tap on another profile switches to it; a .json file dropped on a row is loaded into that
     profile), "Switch profile" (back to the start-up profile choice), the project choice (#profileProjects: on the phone
     the only place to choose it), a note on files and the online-saving section (#driveSection: the services with
     Connect / Disconnect, autosave, the pause message; #driveStatus: what is happening now). Opened with openProfiles()
     (tour, autosync…) or the cloud store; closes with ×, a click outside or Esc (its own step in src/features/keyboard.ts).
     The save state (SYNC, DRIVE, the autosave pause) is the sync store (src/stores/sync.ts, reactive); the rows also follow
     ui.tick (refreshVue, called where the state changes). Logic in src/features/cloud/index.ts, actions in src/stores/cloud.ts. -->
<script setup lang="ts">
import { computed, ref } from 'vue'
import { hasFiles } from '../../utils/image'
import AppDialog from '../AppDialog.vue'
import ProjectChoiceList from '../ProjectChoiceList.vue'
import { useI18n } from '../../composables/useI18n'
import { useDataStore } from '../../stores/data'
import { useUiStore } from '../../stores/ui'
import { useProjectsStore } from '../../stores/projects'
import { useSettingsStore } from '../../stores/settings'
import { useDialogsStore } from '../../stores/dialogs'
import { useCloudStore } from '../../stores/cloud'
import { usePickerStore } from '../../stores/picker'
import { SYNC_LINE_LOOK, cloudState, orList, syncLine, type CloudInfo } from '../../features/cloud'
import { SOCIALS } from '../../data/profiles'
import { profileInitial } from '../../features/profiles'
import { LOCALE } from '../../i18n'
import { useSyncStore } from '../../stores/sync'
import { CS, cloudOn, driveOnline } from '../../features/cloud/common'
import { CLOUDS, CLOUD_ORDER } from '../../features/cloud/services'
import { fmtWhen, syncOf } from '../../features/storage/sync'
import { applyLoadedFile, importInto, saveJson, validProfileFile } from '../../features/storage/files'
import { dropFile } from '../../features/drop'
import { uiAlert } from '../../stores/notices'
import type { Profile } from '../../data/types'
import { app } from '../../features/app'
import { bgImage } from '../../utils/css'
import { esc, socialLinks, stopLinkClick } from '../../utils/html'
import { CONFIG } from '../../config'

const emit = defineEmits<{ close: [] }>()
const { t } = useI18n()
const store = useDataStore()
const ui = useUiStore()
const projects = useProjectsStore()
const settings = useSettingsStore()
const cloud = useCloudStore()
const picker = usePickerStore()
const sync = useSyncStore()

/** The online services Hideo can use, with their state on this device */
function clouds(): CloudInfo[] {
  return CLOUD_ORDER.map(id => {
    const P = CLOUDS[id]
    return { id, name: P.name, icon: P.icon, configured: P.configured(), on: cloudOn(id), email: CS(id).email || '' }
  })
}

// Recomputed when the save state changes (sync store, sync.ver), at every save (store.rev) and refresh (ui.tick)
const rows = computed(() => {
  void store.rev; void ui.tick; void sync.ver
  return store.profiles.list.map(p => {
    const s = syncOf(p.id), f = s.file
    const line = syncLine(s, (CLOUDS[f?.provider || ''] || CLOUDS.google).name, fmtWhen)
    return {
      p, active: p.id === store.profiles.current, line, look: SYNC_LINE_LOOK[line.tone],
      socials: SOCIALS.some(k => p[k]) ? socialLinks(p, 'w-6 h-6', ' text-[10px]') : ''
    }
  })
})
const avatarStyle = (p: Profile) => bgImage(p.avatar)

// Project choice (ProjectChoiceList.vue, the same list as the project menu at the top right)
function manageProjects() {
  projects.closeMenu()
  emit('close')
  useDialogsStore().show('projects', {})
}

// Online saving
const online = driveOnline
const services = computed(() => { void ui.tick; void sync.ver; return clouds() })
const intro = computed(() => t('Save your profiles (with images and voice notes) to {services} and load them on your other devices: every profile has <b>Save online</b> and <b>Load online</b>. The app only sees the files it creates.',
  { services: orList(services.value.map(c => '<b>' + c.name + '</b>'), LOCALE) }))
const autoText = computed(() => settings.values.autoSaveMin
  ? t('Autosave: <b>right after every change</b>, on the services where the profile has already been saved. When you open it, the latest version saved on another device arrives by itself.')
  : t('Autosave: <b>off</b>, on the services where the profile has already been saved. When you open it, the latest version saved on another device arrives by itself.'))
const paused = computed(() => sync.paused)
const onlineOnlyHtml = t('Online saving only works from the {site} website, not when you open the file from your computer.', { site: `<a href="${esc(CONFIG.siteUrl)}" target="_blank" rel="noopener" class="text-brand underline">${esc(CONFIG.siteName)}</a>` })
function changeAutosave() { emit('close'); settings.open('saves') }
function help() { emit('close'); app.switchTab('help') }

// A .json file dropped on a profile row is loaded into that profile
const dragPid = ref('')
const rowOf = (e: DragEvent) => ((e.target as Element | null)?.closest?.('[data-pid]') as HTMLElement | null)
function onDragOver(e: DragEvent) {
  const r = rowOf(e)
  if (r && hasFiles(e)) { e.preventDefault(); dragPid.value = r.dataset.pid || '' }
}
function onDragLeave(e: DragEvent) {
  if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node | null)) dragPid.value = ''
}
async function onDrop(e: DragEvent) {
  const r = rowOf(e), f = dropFile(e)
  dragPid.value = ''
  if (!r || !f) return
  e.preventDefault()
  let d: unknown = null
  try { d = JSON.parse(await f.text()); if (!validProfileFile(d)) throw new Error('invalid') } catch { return uiAlert(t('This file is not a Hideo save file (.json).')) }
  await applyLoadedFile(r.dataset.pid || '', d, 'caricato', 'file')
}
</script>

<template>
  <AppDialog id="profileModal" name="profiles" :title="t('Profiles')" width="max-w-md" :back="false" outside body-class="p-4 space-y-3 overflow-y-auto flex-1 text-sm" @close="emit('close')">
    <p class="text-xs text-gray-500">{{ t('Each profile has its own hooks, videos, calendar, creators, sources and scripts.') }}</p>
    <div id="profileList" class="space-y-2" @dragover="onDragOver" @dragleave="onDragLeave" @drop="onDrop">
      <div v-for="r in rows" :key="r.p.id" :data-pid="r.p.id"
        :class="['flex items-center gap-3 p-3 rounded-xl border transition', r.active ? 'border-brand bg-brand/10' : 'border-gray-800 hover:border-brand/40 cursor-pointer', { 'drag-over': dragPid === r.p.id }]"
        @click="r.active || picker.switchTo(r.p.id)">
        <span v-if="r.p.avatar" :class="['w-10 h-10', r.active ? 'bg-brand text-white' : 'bg-darkbtn text-gray-300', 'block rounded-full bg-cover bg-center flex-shrink-0']" :style="avatarStyle(r.p)"></span>
        <span v-else :class="['w-10 h-10', r.active ? 'bg-brand text-white' : 'bg-darkbtn text-gray-300', 'rounded-full flex items-center justify-center font-bold flex-shrink-0']">{{ profileInitial(r.p) }}</span>
        <span class="flex-1 min-w-0">
          <span class="block font-semibold text-gray-100 truncate">{{ r.p.name }}</span>
          <span v-if="r.p.bio" class="block text-[11px] text-gray-400 truncate">{{ r.p.bio }}</span>
          <span v-if="r.socials" class="flex gap-1.5 my-1" v-html="r.socials" @click="stopLinkClick"></span>
          <span :class="['block text-[11px]', r.look.cls]"><i :class="['fas', r.look.icon, 'mr-1']"></i>{{ r.line.text }}</span>
          <span v-if="online" class="flex flex-wrap gap-1.5 mt-1.5">
            <button type="button" class="px-2.5 py-1 rounded-full bg-darkbtn text-[11px] font-bold text-gray-300 hover:text-brand" @click.stop="cloud.openPick('save', r.p.id)"><i class="fas fa-cloud-arrow-up mr-1"></i>{{ t('Save online') }}</button>
            <button type="button" class="px-2.5 py-1 rounded-full bg-darkbtn text-[11px] font-bold text-gray-300 hover:text-brand" @click.stop="cloud.openPick('load', r.p.id)"><i class="fas fa-cloud-arrow-down mr-1"></i>{{ t('Load online') }}</button>
          </span>
          <span v-if="r.active" class="text-[11px] text-brand font-bold">{{ t('Active profile') }}</span>
          <span v-else class="text-[11px] text-gray-500">{{ t('Tap to switch to this profile') }}</span>
        </span>
        <button type="button" :title="t('Edit profile')" class="text-gray-400 hover:text-brand px-1.5" @click.stop="picker.edit(r.p.id)"><i class="fas fa-pen text-xs"></i></button>
        <button type="button" :title="t('Save the profile with all its data in a file')" class="text-gray-400 hover:text-brand px-1.5" @click.stop="saveJson(r.p.id)"><i class="fas fa-floppy-disk text-xs"></i></button>
        <button type="button" :title="t('Load a file into this profile')" class="text-gray-400 hover:text-brand px-1.5" @click.stop="importInto(r.p.id)"><i class="fas fa-file-import text-xs"></i></button>
        <button v-if="!r.active" type="button" :title="t('Delete profile')" class="text-gray-400 hover:text-brand px-1.5" @click.stop="picker.remove(r.p.id)"><i class="fas fa-trash text-xs"></i></button>
      </div>
    </div>
    <!-- Back to the profile picker (where "+" creates a new profile) -->
    <button type="button" class="w-full bg-brand hover:bg-brandDark text-white py-3 rounded-xl font-bold text-sm" @click="picker.change()"><i class="fas fa-users mr-1.5"></i><span>{{ t('Switch profile') }}</span></button>
    <!-- The profile's active project: on the phone this is the only place to choose it -->
    <div id="profileProjects" class="pt-3 border-t border-gray-800 space-y-1 empty:hidden"><ProjectChoiceList @manage="manageProjects" /></div>
    <p class="text-[11px] text-gray-500"><i class="fas fa-floppy-disk text-brand"></i> <span>{{ t('saves the profile with all its data to a file (plan, calendar, hooks, creators, sources, scripts, photos and bio)') }}</span> · <i class="fas fa-file-import text-brand"></i> <span>{{ t('loads a file into the profile.') }}</span> <span>{{ t('How to use the app on computer and phone:') }}</span> <button type="button" class="text-brand underline" @click="help">{{ t('How it works') }}</button>.</p>
    <div class="pt-3 border-t border-gray-800 space-y-2">
      <div class="text-xs font-bold text-gray-400 tracking-wider">{{ t('ONLINE SAVING') }}</div>
      <div id="driveSection" class="space-y-2">
        <p v-if="!online" class="text-xs text-gray-500" v-html="onlineOnlyHtml"></p>
        <template v-else>
          <p class="text-[11px] text-gray-500" v-html="intro"></p>
          <div class="space-y-2">
            <div v-for="c in services" :key="c.id" class="flex items-center gap-3 p-2.5 rounded-xl border border-gray-800">
              <span :class="['w-9 h-9 rounded-lg bg-darkbtn flex items-center justify-center', c.on ? 'text-green-400' : 'text-gray-500']"><i :class="c.icon"></i></span>
              <span class="flex-1 min-w-0">
                <span class="block text-sm font-semibold text-gray-100">{{ c.name }}</span>
                <span :class="['block text-[11px]', c.on ? 'text-green-400' : 'text-gray-500', 'truncate']">{{ cloudState(c) }}</span>
              </span>
              <template v-if="c.configured">
                <button v-if="c.on" type="button" class="text-[11px] font-bold text-gray-400 hover:text-brand" @click="cloud.disconnect(c.id)"><i class="fas fa-link-slash mr-1"></i>{{ t('Disconnect') }}</button>
                <button v-else type="button" class="px-3 py-1.5 rounded-lg bg-darkbtn text-[11px] font-bold text-gray-300 hover:text-brand" @click="cloud.connect(c.id)">{{ t('Connect') }}</button>
              </template>
            </div>
          </div>
          <p class="text-[11px] text-gray-500"><span v-html="autoText"></span> <button type="button" class="text-brand underline" @click="changeAutosave">{{ t('change') }}</button></p>
          <p v-if="paused" class="text-[11px] text-brand font-semibold"><i class="fas fa-circle-pause mr-1"></i>{{ paused }}</p>
        </template>
      </div>
      <div id="driveStatus" :class="[cloud.status ? '' : 'hidden', 'text-xs text-brand font-semibold']">{{ cloud.status }}</div>
    </div>
  </AppDialog>
</template>
